import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment as ENV } from "@environments/environment";

export interface TotpEnrollmentResponse {
  secret: string;
  barcode_uri: string;
  recovery_codes: string[];
  authenticator_type: string;
}

export interface Authenticator {
  id: string;
  authenticator_type: string;
  active: boolean;
  name?: string;
}

export interface MfaChallengeResponse {
  challenge_type: string;
  oob_code: string;
  binding_method: string;
}

@Injectable({
  providedIn: "root",
})
export class MfaService {
  private readonly auth0Domain = ENV.auth0.domain;
  private readonly mfaApiUrl = `https://${this.auth0Domain}/mfa/associate`;
  private readonly authenticatorsUrl = `https://${this.auth0Domain}/mfa/authenticators`;
  private readonly challengeUrl = `https://${this.auth0Domain}/mfa/challenge`;
  private readonly tokenUrl = `https://${this.auth0Domain}/oauth/token`;

  constructor(private http: HttpClient) {}

  async associateTotp(token: string): Promise<TotpEnrollmentResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    });

    const body = {
      client_id: ENV.auth0.clientId,
      authenticator_types: ["otp"],
    };

    return await firstValueFrom(
      this.http.post<TotpEnrollmentResponse>(this.mfaApiUrl, body, { headers })
    );
  }

  async listAuthenticators(token: string): Promise<Authenticator[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return await firstValueFrom(
      this.http.get<Authenticator[]>(this.authenticatorsUrl, { headers })
    );
  }

  async createChallenge(
    token: string,
    authenticatorId: string
  ): Promise<MfaChallengeResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    });
  
    const body = {
      client_id: ENV.auth0.clientId,
      challenge_type: "otp",
      authenticator_id: authenticatorId,
    };
  
    return await firstValueFrom(
      this.http.post<MfaChallengeResponse>(
        `${this.challengeUrl}`,
        body,
        { headers }
      )
    );
  }

  async verifyEnrollment(token: string, otpCode: string): Promise<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });

    const body = new URLSearchParams({
      grant_type: "http://auth0.com/oauth/grant-type/mfa-otp",
      client_id: ENV.auth0.clientId,
      mfa_token: token,
      otp: otpCode,
    });

    return await firstValueFrom(
      this.http.post<any>(this.tokenUrl, body.toString(), { headers })
    );
  }

  async deleteAuthenticator(
    token: string,
    authenticatorId: string
  ): Promise<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    await firstValueFrom(
      this.http.delete(`${this.authenticatorsUrl}/${authenticatorId}`, {
        headers,
      })
    );
  }
}